import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { 
  Heart, 
  Shield, 
  Users, 
  BookOpen, 
  Briefcase, 
  Scale,
  ChevronRight,
  Quote,
  ArrowRight,
  Calendar,
  Ticket
} from 'lucide-react';
import axios from 'axios';
import { EVENTS } from '../data/events';
import { OFFICIAL_PARTNERS } from '../data/partners';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const { t, language } = useLanguage();
  const [stats, setStats] = useState({
    women_helped: 247,
    children_supported: 183,
    volunteers_active: 45,
    years_of_service: 5
  });
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % 3);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  const testimonials = [
    { id: 'testimony1', quote: t.testimonials.quote1, author: t.testimonials.author1 },
    { id: 'testimony2', quote: t.testimonials.quote2, author: t.testimonials.author2 },
    { id: 'testimony3', quote: t.testimonials.quote3, author: t.testimonials.author3 },
  ];

  const services = [
    { id: 'housing', icon: Shield, title: t.services.housing.title, desc: t.services.housing.desc },
    { id: 'psychological', icon: Heart, title: t.services.psychological.title, desc: t.services.psychological.desc },
    { id: 'legal', icon: Scale, title: t.services.legal.title, desc: t.services.legal.desc },
    { id: 'education', icon: BookOpen, title: t.services.education.title, desc: t.services.education.desc },
    { id: 'reintegration', icon: Briefcase, title: t.services.reintegration.title, desc: t.services.reintegration.desc },
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section 
        data-testid="hero-section"
        className="relative min-h-screen flex items-center"
      >
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.pexels.com/photos/51953/mother-daughter-love-sunset-51953.jpeg)'
          }}
        />
        <div className="absolute inset-0 hero-overlay" />
        
        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="max-w-2xl animate-fade-in-up">
            <h1 
              data-testid="hero-slogan"
              className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight"
            >
              {t.hero.slogan}
            </h1>
            <p className="text-lg sm:text-xl text-white/90 mb-10 leading-relaxed">
              {t.hero.subtitle}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/donate" data-testid="hero-donate-btn">
                <Button className="gold-btn rounded-full px-8 py-6 text-lg font-medium">
                  {t.hero.donateBtn}
                  <Heart className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/volunteer" data-testid="hero-volunteer-btn">
                <Button 
                  variant="outline" 
                  className="rounded-full px-8 py-6 text-lg font-medium border-2 border-white text-white hover:bg-white hover:text-[#1A252C] bg-transparent"
                >
                  {t.hero.volunteerBtn}
                </Button>
              </Link>
              <Link to="/contact" data-testid="hero-help-btn">
                <Button 
                  variant="outline" 
                  className="rounded-full px-8 py-6 text-lg font-medium border-2 border-white/50 text-white hover:bg-white hover:text-[#1A252C] bg-transparent"
                >
                  {t.hero.helpBtn}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-8 h-12 rounded-full border-2 border-white/50 flex justify-center pt-2">
            <div className="w-1 h-3 bg-white/70 rounded-full" />
          </div>
        </div>
      </section>

      {/* Upcoming Event Banner */}
      {EVENTS.some((e) => new Date(e.end) >= new Date()) && (() => {
        const nextEvent = EVENTS.filter((e) => new Date(e.end) >= new Date())
          .sort((a, b) => new Date(a.start) - new Date(b.start))[0];
        const lang = language === 'en' ? 'en' : 'fr';
        const dateLabel = new Date(nextEvent.start).toLocaleDateString(lang === 'fr' ? 'fr-CA' : 'en-CA', {
          day: 'numeric', month: 'long', year: 'numeric',
        });
        return (
          <section data-testid="event-banner" className="bg-gradient-to-r from-[#1A252C] via-[#24333C] to-[#1A252C] border-b-2 border-[#D4AF37]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <div className="text-center sm:text-left">
                    <p className="text-[#D4AF37] text-xs font-medium tracking-widest uppercase mb-1">
                      {lang === 'fr' ? 'Événement à venir' : 'Upcoming event'}
                    </p>
                    <p className="text-white font-serif text-lg sm:text-xl font-semibold">
                      {nextEvent.title[lang]} — {dateLabel}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <Link to="/events" data-testid="event-banner-details-link">
                    <Button variant="outline" className="rounded-full border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white">
                      {lang === 'fr' ? 'En savoir plus' : 'Learn more'}
                    </Button>
                  </Link>
                  <a href={nextEvent.ticketUrl} target="_blank" rel="noopener noreferrer" data-testid="event-banner-tickets-link">
                    <Button className="gold-btn rounded-full">
                      <Ticket className="w-4 h-4 mr-2" />
                      {lang === 'fr' ? 'Billets' : 'Tickets'}
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </section>
        );
      })()}

      {/* Mission Section */}
      <section data-testid="mission-section" className="py-24 gradient-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="animate-fade-in-up">
              <span className="section-label">{t.mission.subtitle}</span>
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#1A252C] mt-4 mb-6">
                {t.mission.title}
              </h2>
              <p className="text-[#546A76] text-lg leading-relaxed mb-6">
                {t.mission.text}
              </p>
              <p className="text-[#546A76] leading-relaxed">
                {t.mission.text2}
              </p>
              <Link to="/about" data-testid="mission-learn-more">
                <Button 
                  variant="outline" 
                  className="mt-8 rounded-full px-6 border-[#4A708B] text-[#4A708B] hover:bg-[#4A708B] hover:text-white"
                >
                  {t.common.learnMore}
                  <ChevronRight className="ml-1 w-4 h-4" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1683650588105-5bff5623df55?w=800"
                alt="Soutien et compassion — accompagnement des femmes victimes de violence au Québec"
                loading="lazy"
                className="rounded-3xl shadow-lg object-cover w-full h-[500px]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview Section */}
      <section data-testid="services-section" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="section-label">{t.services.subtitle}</span>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#1A252C] mt-4">
              {t.services.title}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.slice(0, 3).map((service) => (
              <div
                key={service.id}
                data-testid={`service-card-${service.id}`}
                className="group p-8 rounded-3xl border border-[#E2E8F0] bg-white card-hover"
              >
                <div className="w-14 h-14 rounded-2xl bg-[#4A708B]/10 flex items-center justify-center mb-6 group-hover:bg-[#4A708B] transition-colors">
                  <service.icon className="w-7 h-7 text-[#4A708B] group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-serif text-xl font-semibold text-[#1A252C] mb-3">
                  {service.title}
                </h3>
                <p className="text-[#546A76] leading-relaxed">
                  {service.desc}
                </p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services" data-testid="services-view-all">
              <Button className="primary-btn rounded-full px-8 py-6">
                {t.common.learnMore}
                <ChevronRight className="ml-1 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Impact Stats Section */}
      <section data-testid="impact-section" className="py-24 bg-[#4A708B]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <span className="text-[#D4AF37] text-sm font-semibold tracking-[0.2em] uppercase">
              {t.impact.subtitle}
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-white mt-4 mb-8">
              {t.impact.title}
            </h2>
            <p className="text-white/80 text-lg max-w-2xl mx-auto">
              {t.mission.text2}
            </p>
          </div>
        </div>
      </section>

      {/* Boutique Section */}
      <section data-testid="home-shop-section" className="py-24 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="absolute -top-6 -left-6 w-40 h-40 bg-[#D4AF37]/10 rounded-full blur-2xl" />
              <div className="grid grid-cols-2 gap-4">
                <img
                  src="https://customer-assets-agu9un31.emergentagent.net/job_hope-rebuild-2/artifacts/39a3u68a_IMG_4661.PNG"
                  alt={language === 'fr' ? 'Combinaison sport bleue Fondation Enat' : 'Blue Fondation Enat sport jumpsuit'}
                  loading="lazy"
                  className="rounded-3xl object-cover object-top w-full h-72 sm:h-96 shadow-lg"
                />
                <img
                  src="https://customer-assets-agu9un31.emergentagent.net/job_hope-rebuild-2/artifacts/zve8avnb_IMG_4663.PNG"
                  alt={language === 'fr' ? 'Combinaison sport brune Fondation Enat' : 'Brown Fondation Enat sport jumpsuit'}
                  loading="lazy"
                  className="rounded-3xl object-cover object-top w-full h-72 sm:h-96 shadow-lg mt-8"
                />
              </div>
            </div>
            <div>
              <span className="section-label">{language === 'fr' ? 'BOUTIQUE SOLIDAIRE' : 'CHARITY SHOP'}</span>
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#1A252C] mt-4 mb-6">
                {language === 'fr' ? 'La combinaison sport Fondation Enat' : 'The Fondation Enat Sport Jumpsuit'}
              </h2>
              <p className="text-[#546A76] text-lg leading-relaxed mb-6">
                {language === 'fr'
                  ? 'Confortable, élégante et solidaire : notre combinaison au logo brodé existe en 4 couleurs (noir, beige, brun, bleu) et tailles S à L. Chaque achat finance directement nos services d\'accompagnement.'
                  : 'Comfortable, elegant and charitable: our embroidered-logo jumpsuit comes in 4 colors (black, beige, brown, blue) and sizes S to L. Every purchase directly funds our support services.'}
              </p>
              <div className="flex items-center gap-3 mb-8">
                {['#1A1A1A', '#D8CDBB', '#6A483F', '#416A84'].map((c) => (
                  <span key={c} className="w-8 h-8 rounded-full border-2 border-white shadow-md" style={{ backgroundColor: c }} />
                ))}
                <span className="ml-2 text-2xl font-bold text-[#4A708B]">65$ <span className="text-sm font-medium text-[#546A76]">CAD</span></span>
              </div>
              <Link to="/shop" data-testid="home-shop-cta">
                <Button className="gold-btn rounded-full px-8 py-6 text-base">
                  {language === 'fr' ? 'Découvrir la boutique' : 'Discover the shop'}
                  <ChevronRight className="ml-1 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section - TEMPORARILY HIDDEN
      <section data-testid="testimonials-section" className="py-24 gradient-bg">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="section-label">{t.testimonials.subtitle}</span>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#1A252C] mt-4">
              {t.testimonials.title}
            </h2>
          </div>

          <div className="relative">
            <div className="bg-white rounded-3xl p-10 sm:p-12 soft-shadow">
              <Quote className="w-12 h-12 text-[#D4AF37]/30 mb-6" />
              <p 
                data-testid="testimonial-quote"
                className="font-serif text-xl sm:text-2xl text-[#1A252C] leading-relaxed mb-8"
              >
                "{testimonials[currentTestimonial].quote}"
              </p>
              <p 
                data-testid="testimonial-author"
                className="text-[#546A76] font-medium"
              >
                — {testimonials[currentTestimonial].author}
              </p>
            </div>

            <div className="flex justify-center gap-2 mt-8">
              {testimonials.map((testimonial, index) => (
                <button
                  key={testimonial.id}
                  onClick={() => setCurrentTestimonial(index)}
                  data-testid={`testimonial-dot-${testimonial.id}`}
                  className={`w-3 h-3 rounded-full transition-all ${
                    index === currentTestimonial
                      ? 'bg-[#D4AF37] w-8'
                      : 'bg-[#E2E8F0] hover:bg-[#4A708B]/30'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>
      */}

      {/* Official Partners Section */}
      <section className="py-20 bg-[#FAFAFA]" data-testid="home-partners-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl font-bold text-[#1A252C] mb-3">
            {language === 'fr' ? 'Nos partenaires officiels' : 'Our Official Partners'}
          </h2>
          <p className="text-[#546A76] mb-10 max-w-2xl mx-auto">
            {language === 'fr'
              ? 'Ils soutiennent notre mission auprès des femmes et enfants victimes de violence.'
              : 'They support our mission for women and children victims of violence.'}
          </p>
          <div className="flex flex-wrap justify-center items-center gap-10 mb-10">
            {OFFICIAL_PARTNERS.map((partner) => (
              <a
                key={partner.id}
                href={partner.website}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`home-partner-${partner.id}`}
                title={partner.name}
                className="group flex flex-col items-center"
              >
                <img
                  src={partner.logo}
                  alt={`Logo ${partner.name}`}
                  loading="lazy"
                  className="w-32 h-32 object-contain grayscale-0 transition-transform group-hover:scale-105"
                />
                <span className="text-sm text-[#546A76] mt-2 group-hover:text-[#1A252C] transition-colors">
                  {partner.name}
                </span>
              </a>
            ))}
          </div>
          <Link to="/partners" data-testid="home-partners-cta">
            <Button variant="outline" className="rounded-full border-[#4A708B] text-[#4A708B] hover:bg-[#4A708B]/10">
              {language === 'fr' ? 'Devenir partenaire' : 'Become a partner'}
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="py-24 bg-[#1A252C]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            {t.cta.title}
          </h2>
          <p className="text-white/80 text-lg mb-10 max-w-2xl mx-auto">
            {t.cta.text}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/donate" data-testid="cta-donate-btn">
              <Button className="gold-btn rounded-full px-10 py-6 text-lg font-medium">
                {t.cta.donateBtn}
                <Heart className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/volunteer" data-testid="cta-volunteer-btn">
              <Button 
                variant="outline"
                className="rounded-full px-10 py-6 text-lg font-medium border-2 border-white text-white hover:bg-white hover:text-[#1A252C] bg-transparent"
              >
                {t.cta.volunteerBtn}
                <Users className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
